import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle } from "react-icons/fa";
import TermsCheckbox from "./TermsCheckbox";
interface CourseRegisterModalProps {
  courseId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccessClose: () => void;
}

const CourseRegisterModal: React.FC<CourseRegisterModalProps> = ({
  courseId,
  isOpen,
  onClose,
  onSuccessClose,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  const handleClose = () => {
    if (submitted) {
      onSuccessClose();
    } else {
      onClose();
    }
  };

  // Always call the latest handleClose from the keydown listener below,
  // without re-attaching the listener (and re-running its open/close setup)
  // every time `submitted` changes.
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  const getFocusableElements = (container: HTMLElement) =>
    Array.from(
      container.querySelectorAll<HTMLElement>(
        'a[href], button, textarea, input, select, [tabindex]'
      )
    ).filter((el) => el.tabIndex !== -1 && !el.hasAttribute("disabled"));

  // Focus trap + Escape-to-close + focus restore on close. Keyed only on
  // `isOpen` so it runs exactly once per open/close cycle.
  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedElement.current = document.activeElement as HTMLElement | null;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;

      const focusables = getFocusableElements(modalRef.current);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement.current?.focus();
    };
  }, [isOpen]);

  // Re-focus the first focusable element whenever the modal's content swaps
  // (e.g. form -> success screen), since the previously focused element
  // may have just been unmounted.
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;
    getFocusableElements(modalRef.current)[0]?.focus();
  }, [isOpen, submitted]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!termsAccepted) {
      alert(t("registerModal.termsRequired"));
      return;
    }
    
    const formData = new FormData(e.currentTarget);

    fetch("/api/course_user_mappings", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        course_id: courseId,
        name: formData.get("name"),
        surname: formData.get("surname"),
        email: formData.get("email"),
        mobile_phone: formData.get("mobile_phone"),
        website: formData.get("website"),
      }),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((err) => {
            console.error("Sunucu hatası:", err);
            throw new Error("Kayıt başarısız");
          });
        }
        return res.json();
      })
      .then(() => setSubmitted(true))
      .catch((err) => {
        console.error("Kayıt hatası:", err);
        alert(t("registerModal.error"));
      });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="course-register-modal-title"
        className="bg-gradient-to-b from-black/70 via-zinc-900/80 to-zinc-800/90 text-white p-8 rounded-2xl max-w-md w-full shadow-2xl relative border border-white/20"
      >
        <button
          onClick={handleClose}
          aria-label={t("registerModal.close")}
                      className="absolute top-3 right-4 text-2xl hover:text-brand-red-text cursor-pointer"
        >
          ×
        </button>

        {!submitted && (
          <>
            <p className="text-sm text-gray-300 mb-2 text-center">
              {t("registerModal.intro")}
            </p>
            <h2 id="course-register-modal-title" className="text-2xl font-bold mb-6 text-center">
              {t("registerModal.title")}
            </h2>
          </>
        )}

        {submitted ? (
          <div className="text-center py-10">
            <p className="text-xl font-semibold text-white mb-4 flex items-center justify-center gap-2">
              <FaCheckCircle
                className="text-brand-red-text text-2xl shrink-0"
                aria-hidden="true"
              />
              <span>{t("registerModal.successTitle")}</span>
            </p>
            <p className="text-gray-300">{t("registerModal.successMessage")}</p>
            <button
              onClick={onSuccessClose}
              className="mt-6 px-4 py-2 bg-[#D8001D] text-white rounded hover:bg-[#B8001A] cursor-pointer"
            >
              {t("registerModal.backToHome")}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Honeypot: hidden from sighted/keyboard/AT users, left for bots that fill every field */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute -left-[9999px] w-px h-px overflow-hidden"
            />

            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-200 mb-1">
                {t("registerModal.name")} *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-4 py-2 placeholder-gray-400"
                placeholder={t("registerModal.namePlaceholder")}
              />
            </div>

            <div>
              <label htmlFor="surname" className="block text-sm font-semibold text-gray-200 mb-1">
                {t("registerModal.surname")} *
              </label>
              <input
                id="surname"
                name="surname"
                type="text"
                required
                className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-4 py-2 placeholder-gray-400"
                placeholder={t("registerModal.surnamePlaceholder")}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-200 mb-1">
                {t("registerModal.email")} *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-4 py-2 placeholder-gray-400"
                placeholder="ornek@mail.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-200 mb-1">
                {t("registerModal.phone")} *
              </label>
              <input
                id="phone"
                name="mobile_phone"
                type="tel"
                required
                pattern="^\+?\d{6,15}$"
                className="w-full bg-zinc-800 border border-zinc-600 text-white rounded px-4 py-2 placeholder-gray-400"
                placeholder="+49 176 12345 678"
              />
            </div>

            <TermsCheckbox
              checked={termsAccepted}
              onChange={setTermsAccepted}
              className="mt-2"
            />

            <button
              type="submit"
              className="bg-[#D8001D] hover:bg-[#B8001A] text-white py-2 rounded font-semibold w-full cursor-pointer"
            >
              {t("registerModal.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CourseRegisterModal;